import { PaginatedList, type PaginatedListProps, type PaginatedListScrollbar } from "@tellescope/design-system";
import { useBlockScrollContainer } from "./BlockContent/BlockScrollContainerContext";

export interface BlockPaginatedListProps extends Omit<PaginatedListProps, "scrollOptions"> {
  /** How the scrollbar is presented, when this list owns its scrolling (i.e. outside a block). */
  scrollbar?: PaginatedListScrollbar;
  fadeWhenClipped?: boolean;
}

/**
 * {@link PaginatedList} wired to the enclosing block's scroll container.
 *
 * `BlockContent` already caps its height and scrolls. A `PaginatedList` nested inside it would be a
 * second scroll container that can never overflow — the virtualizer's spacer carries `contain: size`,
 * so its height never reaches the inner element's `scrollHeight` — leaving the list unscrollable and
 * `onBottomReached` (i.e. infinite scroll) permanently silent. Pointing the virtualizer at the block's
 * own scroller keeps a single scroll container and restores pagination.
 *
 * Outside a `BlockContent` this behaves exactly like `PaginatedList`.
 */
export const BlockPaginatedList = ({ scrollbar, fadeWhenClipped, ...props }: BlockPaginatedListProps) => {
  const scrollElement = useBlockScrollContainer();

  // `undefined` means there is no enclosing block, so this list keeps owning its scrolling — which
  // has to stay out of `scrollOptions` entirely, since passing `element: undefined` explicitly would
  // read as "an ancestor scrolls but has not attached yet" and leave the list unscrollable.
  if (scrollElement === undefined) {
    return <PaginatedList {...props} scrollOptions={{ type: scrollbar, fadeWhenClipped }} />;
  }

  return <PaginatedList {...props} scrollOptions={{ element: scrollElement, measureStartMargin: true }} />;
};
